import { useState, type JSX } from "react";
import { Input, type InputProps } from "@linkhr/design-system";
import { parseMoney } from "./parseMoney";

export interface MoneyInputProps extends Omit<
  InputProps,
  "value" | "onChange" | "type" | "inputMode" | "bidi"
> {
  /** A decimal string, or "" when empty. Read once; the field keeps what is typed. */
  value: string;
  /** The amount at the currency's scale, or null while the text is not an amount. */
  onChange: (value: string | null) => void;
  currency: string;
  allowNegative?: boolean;
}

/**
 * An amount field: decimal strings in and out, never a float. Arabic-Indic
 * digits are accepted; on blur a valid amount is shown at the currency's scale.
 */
export function MoneyInput({
  value,
  onChange,
  currency,
  allowNegative = false,
  invalid,
  onBlur,
  ...rest
}: MoneyInputProps): JSX.Element {
  const [text, setText] = useState(value);
  const parsed = parseMoney(text, currency, allowNegative);
  return (
    <Input
      {...rest}
      inputMode="decimal"
      bidi="ltr"
      value={text}
      invalid={invalid === true || (text.trim() !== "" && parsed === null)}
      onChange={(event) => {
        setText(event.target.value);
        onChange(parseMoney(event.target.value, currency, allowNegative));
      }}
      onBlur={(event) => {
        if (parsed !== null) setText(parsed);
        onBlur?.(event);
      }}
    />
  );
}
